import { Component } from '@theme/component';

const MIN_QUERY_LENGTH = 2;
/** The most the search asks for when a chip filters its results in the browser (the search itself returns up to 10). */
const FILTERED_RESULTS = 10;
const SEARCH_DELAY = 250;

/**
 * @typedef {{ state: string, metafield: string, type: string }} Filter - The availability the chips filter by
 *   ("in_stock", "on_demand" or "", with the value stored in the `pcero.disponibilidad` metafield) and the protection
 *   type ("Corte", "Frío"... or ""); both apply at once.
 */

/**
 * Product search of the shop (`snippets/pc-search.liquid`), the same experience as the model picker of the quote builder
 * (`assets/pc-quote.js`): a combobox whose panel has two groups of chips, the two filters of the catalog (availability
 * and protection type), and a list of models.
 *
 * Opened without a query it lists the models of the suggested collection; with a query of two or more characters (a
 * name, a SKU, an ANSI level) it asks Shopify's Predictive Search, and a chip without a query lists the whole catalog,
 * rendered by `sections/pc-search-results.liquid`. The chips filter that list in the browser. Requests wait 250 ms after
 * typing, a newer request cancels the older one and an answer that arrives late is dropped. Arrow keys move through the
 * results, Enter opens the chosen one (or the only one), Escape closes the panel, and a click or focus outside closes it.
 * Enter with nothing chosen goes to the search page (or, with chips and nothing typed, to the catalog), with the chips as
 * the catalog's filters.
 *
 * The `inline` mode (header) opens the panel under the field; the `modal` mode (search dialog) keeps it open.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The search form.
 * @property {HTMLInputElement} input - The search combobox.
 * @property {HTMLElement} panel - Chips and results.
 * @property {HTMLElement} chips - Filter chips.
 * @property {HTMLElement} heading - Title of the results.
 * @property {HTMLElement} results - Listbox with the options.
 * @property {HTMLAnchorElement} all - "See all results" link.
 * @property {HTMLElement} status - Live region (results count).
 * @property {HTMLScriptElement} strings - JSON with the translated texts.
 *
 * @extends {Component<Refs>}
 */
class PcSearch extends Component {
  requiredRefs = ['form', 'input', 'panel', 'chips', 'heading', 'results', 'all', 'status', 'strings'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {AbortController | undefined} */
  #searchAbort;

  /** @type {number | undefined} */
  #timer;

  /** Identifies the latest request, so an older answer never replaces a newer one. */
  #requestId = 0;

  /** @type {Filter} */
  #filter = { state: '', metafield: '', type: '' };

  /** Options of a collection (the suggested one, the whole catalog), as the server rendered them, by URL. */
  #browseHtml = new Map();

  /** @type {HTMLElement[]} */
  #options = [];

  #activeIndex = -1;

  /** @type {Record<string, string>} */
  #strings = {};

  /** The terms of the last request: what "See all results" and Enter send to the search page. */
  #terms = '';

  /** Set while the script moves the focus back to the field, which must not open the panel it has just closed. */
  #skipOpen = false;

  get #modal() {
    return this.dataset.mode === 'modal';
  }

  get #maxResults() {
    return Number(this.dataset.maxResults) || 8;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');

    const { form, input, panel, chips } = this.refs;
    input.addEventListener('input', this.#onInput, { signal });
    input.addEventListener('keydown', this.#onInputKeydown, { signal });
    input.addEventListener('focus', this.#onFocus, { signal });
    input.addEventListener('click', this.#onFocus, { signal });
    form.addEventListener('keydown', this.#onFormKeydown, { signal });
    form.addEventListener('focusout', this.#onFocusOut, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    // Keep the focus in the field while the panel is pressed; clicks still reach the links
    panel.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    chips.addEventListener('click', this.#onChipClick, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#searchAbort?.abort();
    window.clearTimeout(this.#timer);
  }

  #onFocus = () => {
    if (this.#skipOpen) return;

    const { panel, results } = this.refs;
    const shown = this.#modal ? results.childElementCount > 0 : !panel.hidden;
    if (!shown) void this.#refresh();
  };

  #onInput = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#refresh(), SEARCH_DELAY);
  };

  /** @param {Event} event */
  #onChipClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('button[data-filter]') : null;
    if (!(chip instanceof HTMLButtonElement)) return;

    // Each group of chips ("availability" or "type") sets its own part of the filter; the group's first chip clears it
    this.#filter =
      chip.dataset.filter === 'type'
        ? { ...this.#filter, type: chip.dataset.value ?? '' }
        : { ...this.#filter, state: chip.dataset.value ?? '', metafield: chip.dataset.metafield ?? '' };
    for (const other of chip.closest('.pc-search__chips')?.querySelectorAll('button[data-filter]') ?? []) {
      other.setAttribute('aria-pressed', String(other === chip));
    }

    void this.#refresh();
  };

  get #filtering() {
    return Boolean(this.#filter.state || this.#filter.type);
  }

  /**
   * Shows the options that match the query and the chips: the search of the shop for a query, the whole catalog when a
   * chip filters without a query (as the catalog filter does), the suggested collection when there is neither.
   */
  async #refresh() {
    const { input, panel } = this.refs;
    const query = input.value.trim();
    const { state, type } = this.#filter;
    const requestId = ++this.#requestId;

    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');

    const terms = query.length >= MIN_QUERY_LENGTH ? query : '';
    this.#terms = terms;

    try {
      const catalog = !terms && this.#filtering && Boolean(this.dataset.catalogUrl);
      const browsing = !terms && !catalog && Boolean(this.dataset.suggestedUrl);
      const limit = this.#filtering ? FILTERED_RESULTS : this.#maxResults;
      let options = terms
        ? await this.#fetchOptions(terms, limit)
        : catalog
          ? await this.#fetchBrowse(this.dataset.catalogUrl)
          : browsing
            ? await this.#fetchBrowse(this.dataset.suggestedUrl)
            : [];
      if (requestId !== this.#requestId) return;

      // Each result carries how the model is offered (see snippets/pc-availability-offer.liquid) and its protection types
      if (state) options = options.filter((option) => option.dataset.state === state);
      if (type) options = options.filter((option) => (option.dataset.types ?? '').split('|').includes(type));
      this.#showOptions(options, browsing);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (requestId === this.#requestId) this.refs.status.textContent = this.#strings.searchError;
    }
  }

  /**
   * @param {string} terms
   * @param {number} limit
   */
  async #fetchOptions(terms, limit) {
    const url = new URL(this.dataset.searchUrl ?? '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(limit));
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('section_id', this.dataset.resultsSection ?? 'pc-search-results');

    return this.#parseOptions(await this.#fetchText(url));
  }

  /**
   * @param {string | undefined} collectionUrl
   * @returns {Promise<HTMLElement[]>} The models of a collection.
   */
  async #fetchBrowse(collectionUrl) {
    const key = collectionUrl ?? '';
    let html = this.#browseHtml.get(key);
    if (html === undefined) {
      const url = new URL(key, window.location.origin);
      url.searchParams.set('section_id', this.dataset.resultsSection ?? 'pc-search-results');
      html = await this.#fetchText(url);
      this.#browseHtml.set(key, html);
    }

    return this.#parseOptions(html);
  }

  /** @param {URL} url */
  async #fetchText(url) {
    this.#searchAbort?.abort();
    this.#searchAbort = new AbortController();

    const response = await fetch(url, { signal: this.#searchAbort.signal });
    if (!response.ok) throw new Error(String(response.status));
    return response.text();
  }

  /**
   * The ids of the options are the same in every instance (header and dialog), so each one gets its own prefix.
   * @param {string} text
   * @returns {HTMLElement[]}
   */
  #parseOptions(text) {
    const html = new DOMParser().parseFromString(text, 'text/html');
    const options = /** @type {HTMLElement[]} */ ([...html.querySelectorAll('[data-pc-search-results] [role="option"]')]);
    for (const option of options) option.id = `${this.refs.input.id}-${option.id}`;
    return options;
  }

  /**
   * @param {HTMLElement[]} options
   * @param {boolean} browsing - Whether these are the suggested models and not the answer to a search.
   */
  #showOptions(options, browsing) {
    const { results, input, status, heading, all } = this.refs;
    this.#options = options;
    this.#activeIndex = -1;

    if (options.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'pc-quote-option pc-quote-option--empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = this.#strings.noResults;
      results.replaceChildren(empty);
      status.textContent = this.#strings.noResults;
      heading.hidden = true;
    } else {
      results.replaceChildren(...options);
      const count = (options.length === 1 ? this.#strings.resultsOne : this.#strings.resultsOther).replace(
        /\{qty\}/g,
        () => String(options.length)
      );
      heading.textContent = browsing ? this.#strings.bestSellers : count;
      heading.hidden = false;
      status.textContent = count;
    }

    all.hidden = browsing || options.length === 0 || !(this.#terms || this.#filtering);
    all.href = this.#searchUrl();

    input.setAttribute('aria-expanded', 'true');
    input.removeAttribute('aria-activedescendant');
  }

  /**
   * @returns {string} Where "See all results" and the form lead: the search page for the typed terms, or the catalog
   *   when only chips are chosen. The chips go along as the filters of the catalog.
   */
  #searchUrl() {
    const { metafield, type } = this.#filter;
    const typed = Boolean(this.#terms);
    const url = new URL((typed ? this.dataset.searchPageUrl : this.dataset.catalogUrl) ?? '/search', window.location.origin);
    if (typed) {
      url.searchParams.set('q', this.#terms);
      url.searchParams.set('options[prefix]', 'last');
    }
    // The filters of the catalog (Search & Discovery) narrow the page too, once they are set up there
    if (metafield) url.searchParams.set('filter.p.m.pcero.disponibilidad', metafield);
    if (type) url.searchParams.set('filter.p.m.pcero.tipos_proteccion', type);
    return url.toString();
  }

  /** Closes the panel of the header; the dialog's stays open. */
  #closePanel() {
    if (this.#modal) return;

    window.clearTimeout(this.#timer);
    this.#requestId++;
    this.#searchAbort?.abort();
    const { panel, input, results } = this.refs;
    panel.hidden = true;
    results.replaceChildren();
    this.#options = [];
    this.#activeIndex = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  /** @param {number} index */
  #setActive(index) {
    if (this.#options.length === 0) return;

    const next = (index + this.#options.length) % this.#options.length;
    this.#options.forEach((option, position) => {
      const active = position === next;
      option.toggleAttribute('data-active', active);
      option.setAttribute('aria-selected', String(active));
    });

    this.#activeIndex = next;
    const option = this.#options[next];
    this.refs.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  /** @param {HTMLElement} option */
  #open(option) {
    const url = option.dataset.url;
    if (url) window.location.assign(url);
  }

  /** @param {KeyboardEvent} event */
  #onInputKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.refs.panel.hidden) void this.#refresh();
        else this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#activeIndex < 0 ? this.#options.length - 1 : this.#activeIndex - 1);
        break;
      case 'Enter':
        if (event.isComposing) break;
        // A result chosen with the arrows (or the only one) opens; otherwise the form goes to the search page
        if (this.#activeIndex >= 0) {
          event.preventDefault();
          this.#open(this.#options[this.#activeIndex]);
        } else if (this.#options.length === 1 && this.#terms) {
          event.preventDefault();
          this.#open(this.#options[0]);
        }
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #onFormKeydown = (event) => {
    if (event.key !== 'Escape' || this.#modal || this.refs.panel.hidden) return;

    event.preventDefault();
    this.#closePanel();
    // From a chip the focus comes back to the field, and that must not open the panel again
    this.#skipOpen = true;
    this.refs.input.focus({ preventScroll: true });
    this.#skipOpen = false;
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#closePanel();
  };

  /** @param {Event} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closePanel();
  };

  /**
   * The form goes to the search page, or to the catalog when only chips are chosen; the chips go along as the filters of
   * the catalog, which a plain form submit would not carry. An empty search with no chip goes nowhere.
   * @param {Event} event
   */
  #onSubmit = (event) => {
    const query = this.refs.input.value.trim();
    if (!query && !this.#filtering) {
      event.preventDefault();
      return;
    }

    if (this.#filtering) {
      event.preventDefault();
      this.#terms = query;
      window.location.assign(this.#searchUrl());
    }
  };
}

if (!customElements.get('pc-search')) {
  customElements.define('pc-search', PcSearch);
}
